import { Injectable, createInterfaceId } from "@gitlab/needle";
import { camelCase, cloneDeep, debounce, isEmpty, isEqual, isPlainObject, snakeCase, sortBy, throttle } from "lodash-es";
import { Logger, logCtxItem, logCtxParent, withPrefix } from "@gitlab-org/logging";
import { ClientError, GraphQLClient, gql } from "graphql-request";
import EventEmitter from "events";
import { coerce, gte, valid } from "semver";
import { Buffer } from "buffer";
import { declareRequest } from "@gitlab-org/rpc";
import { z } from "zod";
import { NotificationType } from "vscode-languageserver-protocol";
//#region src/gitlab_api/service.ts
const GitLabApiService = createInterfaceId("GitLabApiService");
//#endregion
//#region src/gitlab_api/graphql_query_identifier_utils.ts
/**
* Extracts the first non-empty line from a GraphQL query. We expect this to be the operation type and name.
*/
function extractGraphQLQueryIdentifier(query) {
	return (query.split("\n").find((s) => s.trim()) ?? query.replace("\n", "")).trim();
}
/**
* Extracts the operation type (query/mutation/subscription) from a GraphQL query.
* Returns undefined if no valid operation type is found.
*/
function extractGraphQLOperationType(query) {
	return extractGraphQLQueryIdentifier(query).match(/^(query|mutation|subscription)\s+/)?.[1];
}
/**
* Extracts the operation name from a GraphQL query.
* Returns undefined if no operation name is found.
*/
function extractGraphQLOperationName(query) {
	return extractGraphQLQueryIdentifier(query).match(/(?:query|mutation|subscription)\s+(\w+)/)?.[1];
}
/**
* Extracts a formatted operation label (e.g., "query: getUser") from a GraphQL query.
* Returns undefined if the query doesn't have a valid operation.
*/
function extractGraphQLOperationLabel(query) {
	const operationType = extractGraphQLOperationType(query);
	const operationName = extractGraphQLOperationName(query);
	if (operationType) return `${operationType}: ${operationName ?? "anonymous"}`;
}
//#endregion
//#region src/gitlab_api/types/request_to_context.ts
const bodyToContext = (restRequest) => {
	if (restRequest.method === "GET" || restRequest.method === "HEAD") return void 0;
	if (!restRequest.body) return void 0;
	return logCtxItem("Body", JSON.stringify(restRequest.body));
};
const searchParamsToContext = (restRequest) => {
	if (restRequest.method === "POST" || restRequest.method === "PATCH" || restRequest.method === "PUT") return;
	if (!restRequest.searchParams) return void 0;
	return logCtxItem("Search Params", JSON.stringify(restRequest.searchParams));
};
const headersToContext = (restRequest) => {
	if (!restRequest.headers) return void 0;
	return logCtxItem("Headers", JSON.stringify(restRequest.headers));
};
const requestToContext = (request) => {
	switch (request.type) {
		case "graphql": return logCtxParent("GraphQL Request", logCtxItem("Query", request.query.replace(/[\s\n]+/g, " ").trim()), logCtxItem("Variables", JSON.stringify(request.variables)));
		case "rest": return logCtxParent("REST Request", logCtxItem("Method", request.method), logCtxItem("Path", request.path), headersToContext(request), searchParamsToContext(request), bodyToContext(request));
		default: return logCtxItem("Unknown request type", request.type);
	}
};
//#endregion
//#region src/gitlab_api/errors.ts
function isSanitizedError(object) {
	return typeof object === "object" && object !== null && "sanitizedMessage" in object && typeof object.sanitizedMessage === "string";
}
const stackToArray = (stack) => (stack ?? "").split("\n");
const getErrorType = (body) => {
	try {
		return JSON.parse(body)?.error;
	} catch {
		return;
	}
};
const getErrorDescription = (body) => {
	if (!body) return "";
	try {
		return JSON.parse(body)?.error_description ?? "";
	} catch {
		return "";
	}
};
const isInvalidTokenError = (response, body) => Boolean(response.status === 401 && body && getErrorType(body) === "invalid_token");
const isInvalidRefresh = (response, body) => Boolean(response.status === 400 && body && getErrorType(body) === "invalid_grant");
const isMissingDefaultDuoGroupError = (error) => {
	if (!isFetchError(error)) return false;
	if (!error.body) return false;
	try {
		return JSON.parse(error.body).error === "missing_default_duo_group";
	} catch {
		return false;
	}
};
var BaseApiError = class extends Error {
	constructor(message, request, options) {
		super(message);
		this.request = request;
		this.cause = options?.cause;
	}
	/**
	* Returns a sanitized version of the error message that's safe for logging.
	* Subclasses should override this to provide more specific sanitization.
	*/
	get sanitizedMessage() {
		return "API request failed";
	}
};
var BaseResponseError = class extends BaseApiError {
	constructor(message, request, status, options) {
		super(message, request, options);
		this.status = status;
	}
};
var RESTError = class extends BaseResponseError {
	type = "rest";
	#body;
	constructor(request, response, resourceName, body) {
		let message = `Fetching ${resourceName} from ${response.url} failed. ${getErrorDescription(body)}`;
		if (isInvalidTokenError(response, body)) message = `Request for ${resourceName} failed because the token is expired or revoked.`;
		if (isInvalidRefresh(response, body)) message = `Request to refresh token failed, because it's revoked or already refreshed.`;
		super(message, request, response.status);
		this.response = response;
		this.#body = body;
	}
	isInvalidTokenOrInvalidRefresh() {
		return isInvalidTokenError(this.response, this.#body) || isInvalidRefresh(this.response, this.#body);
	}
	get ctx() {
		return logCtxParent("Error details", requestToContext(this.request), this.#responseToContext());
	}
	get sanitizedMessage() {
		if (isInvalidTokenError(this.response, this.#body)) return `Request failed because the token is expired or revoked.`;
		if (isInvalidRefresh(this.response, this.#body)) return `Request to refresh token failed, because it's revoked or already refreshed.`;
		return `Request failed with error code: ${this.response.status}`;
	}
	#responseToContext() {
		const headers = Object.fromEntries(this.response.headers.entries());
		return logCtxParent("Response", logCtxItem("Status", String(this.status)), !isEmpty(headers) ? logCtxItem("Headers", JSON.stringify(headers)) : void 0, this.#body ? logCtxItem("Response body", this.#body) : void 0);
	}
	get body() {
		return this.#body;
	}
};
const getResponseErrorText = (graphQlResponse) => graphQlResponse.errors?.map((e) => e.message).join(",") || "";
var GraphQLError = class extends BaseResponseError {
	type = "graphql";
	#queryIdentifier;
	constructor(request, errorResponse) {
		const queryIdentifier = extractGraphQLOperationLabel(request.query);
		const message = `GraphQL request "${queryIdentifier}" failed with ${getResponseErrorText(errorResponse)}`;
		super(message, request, errorResponse.status);
		this.graphQlResponse = errorResponse;
		this.#queryIdentifier = queryIdentifier;
	}
	get sanitizedMessage() {
		return `GraphQL request "${this.#queryIdentifier}" failed with status ${this.graphQlResponse.status}`;
	}
	get ctx() {
		return logCtxParent("Error details", requestToContext(this.request), this.#responseToContext());
	}
	#responseToContext() {
		return logCtxParent("Response", logCtxItem("Status", String(this.graphQlResponse.status)), logCtxItem("Errors", getResponseErrorText(this.graphQlResponse)));
	}
};
var NetworkError = class extends BaseApiError {
	type = "network";
	constructor(request, cause) {
		const message = cause instanceof Error ? cause.message : `Network error occurred: ${cause}`;
		super(message, request);
		this.cause = cause;
	}
	get ctx() {
		return logCtxParent("Error details", requestToContext(this.request), this.#networkErrorContext());
	}
	get sanitizedMessage() {
		const errorProps = this.cause;
		const errorCode = errorProps?.code || errorProps?.errno;
		const errorDetails = this.#getErrorDescription(errorCode);
		return `Network error occurred${errorDetails ? `: ${errorDetails}` : ""}`;
	}
	#networkErrorContext() {
		if (!this.cause || !(this.cause instanceof Error)) return;
		const errorProps = this.cause;
		if (errorProps.code || errorProps.errno) {
			const errorCode = errorProps.code || errorProps.errno;
			const description = this.#getErrorDescription(errorCode);
			return logCtxItem("Network issue", description ? `${description} (${errorCode})` : `Error code: ${errorCode}`);
		}
	}
	#getErrorDescription(errorCode) {
		return {
			ENOTFOUND: "DNS lookup failed - hostname could not be resolved",
			ECONNREFUSED: "Connection refused - server is not accepting connections",
			ECONNRESET: "Connection reset by peer - server closed the connection unexpectedly",
			ETIMEDOUT: "Connection timed out - server did not respond in time",
			EHOSTUNREACH: "Host unreachable - network route to server not available",
			ENETUNREACH: "Network unreachable - no network connectivity",
			ECONNABORTED: "Connection aborted - request was cancelled",
			EBADF: "Bad file descriptor - network connection issue",
			EPIPE: "Broken pipe - connection closed while writing data",
			EAI_AGAIN: "DNS lookup failed temporarily - try again later"
		}[errorCode];
	}
};
var TimeoutError = class extends BaseApiError {
	type = "timeout";
	#sanitizedError;
	constructor(request, timeout) {
		const message = `Request timed out${timeout ? ` after ${timeout}ms` : ""}`;
		super(message, request);
		this.#sanitizedError = message;
	}
	get ctx() {
		return logCtxParent("Error details", requestToContext(this.request));
	}
	get sanitizedMessage() {
		return this.#sanitizedError;
	}
};
var AbortError = class extends BaseApiError {
	type = "abort";
	constructor(request, reason) {
		const message = `Request was aborted${reason ? `: ${reason}` : ""}`;
		super(message, request);
	}
	get ctx() {
		return logCtxParent("Error details", requestToContext(this.request));
	}
	get sanitizedMessage() {
		return "Request was aborted";
	}
};
function isApiError(object) {
	return isResponseError(object) || isTransportError(object);
}
function isResponseError(object) {
	return object instanceof RESTError || object instanceof GraphQLError;
}
function isTransportError(object) {
	return object instanceof NetworkError || object instanceof TimeoutError || object instanceof AbortError;
}
const FetchError = RESTError;
function isFetchError(object) {
	return object instanceof RESTError;
}
//#endregion
//#region src/gitlab_api/duo_access_error.ts
const DUO_DEFAULT_NAMESPACE_DOCS_URL = "https://docs.gitlab.com/user/profile/preferences/#set-a-default-gitlab-duo-namespace";
const DUO_NAMESPACE_NOT_ENTITLED_MESSAGE = `GitLab Duo Agent Platform is not available for this namespace or project.

This usually means:
- The group does not have a GitLab Duo subscription, or its beta/experimental features are off.
- Personal (user) namespaces are not supported for the Agent Platform.

Next steps:
- Use a project in a group that has GitLab Duo enabled.
- Or set a default GitLab Duo namespace that is entitled for Duo: ${DUO_DEFAULT_NAMESPACE_DOCS_URL}\n- If you believe this group should have access, contact your administrator or group Owner.`;
const DUO_NO_NAMESPACE_DETECTED_MESSAGE = `Could not determine a GitLab Duo namespace.

Set a default GitLab Duo namespace in your user preferences, or run from a project in a Duo-enabled group:\n${DUO_DEFAULT_NAMESPACE_DOCS_URL}`;
const DUO_USAGE_QUOTA_EXCEEDED_MESSAGE = "You don't have enough GitLab Credits to run GitLab Duo Agent Platform.\n\nContact your administrator to purchase more credits, then try again.";
const USAGE_QUOTA_EXCEEDED_MARKER = "USAGE_QUOTA_EXCEEDED";
const isDuoAccessMessage = (message) => message === DUO_NAMESPACE_NOT_ENTITLED_MESSAGE || message === DUO_NO_NAMESPACE_DETECTED_MESSAGE || message === "You don't have enough GitLab Credits to run GitLab Duo Agent Platform.\n\nContact your administrator to purchase more credits, then try again.";
const parseBody = (body) => {
	let parsed = {};
	if (body) try {
		parsed = JSON.parse(body);
	} catch {
		parsed = {};
	}
	return parsed;
};
const classifyDuoAccessError = (status, body) => {
	let result;
	if (status === 403) {
		const { error, message } = parseBody(body);
		if (error === "missing_default_duo_group") result = {
			kind: "no_namespace",
			message: DUO_NO_NAMESPACE_DETECTED_MESSAGE
		};
		else if (typeof message === "string" && message.includes(USAGE_QUOTA_EXCEEDED_MARKER)) result = {
			kind: "quota_exceeded",
			message: DUO_USAGE_QUOTA_EXCEEDED_MESSAGE
		};
		else result = {
			kind: "not_entitled",
			message: DUO_NAMESPACE_NOT_ENTITLED_MESSAGE
		};
	}
	return result;
};
//#endregion
//#region src/gitlab_api/requests/instance_version.ts
const versionRequest = {
	type: "rest",
	method: "GET",
	path: "/api/v4/version"
};
//#endregion
//#region src/gitlab_api/retry_check.ts
const isNot4xxFailure = (err) => isFetchError(err) && (err.status < 400 || err.status >= 500);
//#endregion
//#region src/fetch/handle_fetch_error.ts
const handleFetchError = async (request, response, resourceName) => {
	if (!response.ok) throw new FetchError(request, response, resourceName, await response.text().catch(() => void 0));
};
//#endregion
//#region src/get_language_server_version.ts
function getLanguageServerVersion() {
	return BUNDLER_INJECTED_GITLAB_LANGUAGE_SERVER_VERSION;
}
//#endregion
//#region src/utils/get_user_agent.ts
const getUserAgent = (clientInfo) => {
	const clientInfoString = clientInfo ? `${clientInfo?.name}:${clientInfo?.version}` : "missing client info";
	return `gitlab-language-server:${getLanguageServerVersion()} (${clientInfoString})`;
};
//#endregion
//#region src/utils/not_null_or_undefined.ts
/**
* This function is [user-defined type guard](https://www.typescriptlang.org/docs/handbook/advanced-types.html#user-defined-type-guards)
* used for narrowing down type for common collection.filter() use case.
*
* - Open GitHub issue: https://github.com/microsoft/TypeScript/issues/16069#issuecomment-369374214
*/
function notNullOrUndefined(value) {
	return value !== null && value !== void 0;
}
//#endregion
//#region src/utils/url.ts
const addQueryParams = (url, query) => {
	Object.entries(query).forEach(([name, value]) => {
		if (notNullOrUndefined(value)) url.searchParams.append(name, String(value));
	});
	return url;
};
const ensureEndsWithSlash = (url) => url.toString().replace(/\/?$/, "/");
const ensureRelativePath = (path) => path.replace(/^\.?\/?/, "./");
/**
* Handle various git remote formats (http, ssh etc) and convert them to http.
* @param remoteUrl - The git remote URL to convert
* @returns The HTTP(S) equivalent of the remote URL
*/
const convertToHttpUrl = (remoteUrl) => {
	if (remoteUrl.startsWith("git@")) {
		const match = remoteUrl.match(/git@([^:]+):(.+)/);
		if (match) {
			const [, domain, path] = match;
			return `https://${domain}/${path}`;
		}
	}
	if (remoteUrl.startsWith("git://")) return remoteUrl.replace("git://", "https://");
	if (remoteUrl.startsWith("ssh://")) return remoteUrl.replace("ssh://", "https://").replace(/git@([^/]+)/, "$1");
	return remoteUrl;
};
//#endregion
//#region src/gitlab_api/simple_api_client.ts
const getResourceName = (path) => path.split("/").pop() || "unknown resource";
var DefaultSimpleApiClient = class {
	#logger;
	#lsFetch;
	#baseUrl;
	#token;
	#clientInfo;
	/** JSON-encoded environment context, forwarded via `X-Gitlab-Tracking-Context`. */
	#trackingContext;
	#fetchOptions;
	constructor(logger, lsFetch, clientInfo, baseUrl, token, trackingContext, fetchOptions) {
		this.#logger = logger;
		this.#lsFetch = lsFetch;
		this.#baseUrl = new URL(ensureEndsWithSlash(baseUrl));
		this.#token = token;
		this.#clientInfo = clientInfo;
		this.#trackingContext = trackingContext;
		this.#fetchOptions = fetchOptions;
	}
	getGitlabClientHeaders(clientInfo) {
		return {
			"X-Gitlab-Client-Name": clientInfo?.name ?? "unknown",
			"X-Gitlab-Client-Version": clientInfo?.version ?? "unknown"
		};
	}
	getDefaultHeaders() {
		return {
			Authorization: `Bearer ${this.#token}`,
			"User-Agent": getUserAgent(this.#clientInfo),
			"X-Gitlab-Language-Server-Version": getLanguageServerVersion(),
			...this.getGitlabClientHeaders(this.#clientInfo),
			...this.#trackingContext ? { "X-Gitlab-Tracking-Context": this.#trackingContext } : {}
		};
	}
	async fetchFromApiRaw(request) {
		try {
			const unknownRequestMethodError = `Unknown request method "${request.method}", request type "${request.type}"`;
			switch (request.method) {
				case "GET": return await this.#fetchRaw(request);
				case "POST": return await this.#postFetchRaw(request);
				case "PATCH": return await this.#patchFetchRaw(request);
				case "PUT": return await this.#putFetchRaw(request);
				case "HEAD": return await this.#headFetchRaw(request);
				default: throw new Error(unknownRequestMethodError);
			}
		} catch (e) {
			throw new NetworkError(request, e);
		}
	}
	async fetchFromApi(request) {
		if (request.type === "graphql") return this.#graphqlRequest(request);
		const response = await this.fetchFromApiRaw(request);
		await handleFetchError(request, response, getResourceName(request.path));
		return response.json();
	}
	fetchOperation = (request) => (signal) => this.fetchFromApi({
		...request,
		signal
	});
	async #graphqlRequest(request) {
		const endpoint = new URL("./api/graphql", this.#baseUrl);
		const operationLabel = extractGraphQLOperationLabel(request.query);
		if (operationLabel) this.#logger.debug(`[SimpleApiClient] Making GraphQL request: ${operationLabel}`);
		const graphqlFetch = async (input, init) => {
			const url = input instanceof URL ? input.toString() : input;
			return this.#lsFetch.post(url, {
				...init,
				headers: { ...init?.headers },
				signal: request.signal
			}, ...this.#fetchOptionsArgs());
		};
		const client = new GraphQLClient(endpoint.href, {
			fetch: graphqlFetch,
			headers: { ...this.getDefaultHeaders() }
		});
		try {
			return await client.request(request.query, request.variables);
		} catch (error) {
			if (error instanceof ClientError) throw new GraphQLError(request, error.response);
			throw new NetworkError(request, error);
		}
	}
	async #fetchRaw(request) {
		const resourcePath = ensureRelativePath(request.path);
		const url = new URL(resourcePath, this.#baseUrl);
		addQueryParams(url, request.searchParams ?? {});
		return this.#lsFetch.get(url, {
			headers: {
				...this.getDefaultHeaders(),
				...request.headers
			},
			signal: request.signal
		}, ...this.#fetchOptionsArgs());
	}
	async #postFetchRaw(request) {
		const resourcePath = ensureRelativePath(request.path);
		const url = new URL(resourcePath, this.#baseUrl);
		const requestParams = {
			headers: {
				"Content-Type": "application/json",
				...this.getDefaultHeaders(),
				...request.headers
			},
			body: JSON.stringify(request.body),
			signal: request.signal
		};
		return this.#lsFetch.post(url, requestParams, ...this.#fetchOptionsArgs());
	}
	async #patchFetchRaw(request) {
		const resourcePath = ensureRelativePath(request.path);
		const url = new URL(resourcePath, this.#baseUrl);
		return this.#lsFetch.patch(url, {
			headers: {
				"Content-Type": "application/json",
				...this.getDefaultHeaders(),
				...request.headers
			},
			body: JSON.stringify(request.body),
			signal: request.signal
		}, ...this.#fetchOptionsArgs());
	}
	async #putFetchRaw(request) {
		const resourcePath = ensureRelativePath(request.path);
		const url = new URL(resourcePath, this.#baseUrl);
		return this.#lsFetch.put(url, {
			headers: {
				"Content-Type": "application/json",
				...this.getDefaultHeaders(),
				...request.headers
			},
			body: JSON.stringify(request.body),
			signal: request.signal
		}, ...this.#fetchOptionsArgs());
	}
	async #headFetchRaw(request) {
		const resourcePath = ensureRelativePath(request.path);
		const url = new URL(resourcePath, this.#baseUrl);
		addQueryParams(url, request.searchParams ?? {});
		return this.#lsFetch.head(url, {
			headers: {
				...this.getDefaultHeaders(),
				...request.headers
			},
			signal: request.signal
		}, ...this.#fetchOptionsArgs());
	}
	#fetchOptionsArgs() {
		return this.#fetchOptions ? [this.#fetchOptions] : [];
	}
};
//#endregion
//#region src/gitlab_api/token_validation.ts
const REQUIRED_SCOPES = [
	"ai_features",
	"read_api",
	"api"
];
const handleTokenCheckError = (e) => {
	if (!(e instanceof RESTError) || !e.isInvalidTokenOrInvalidRefresh()) return {
		valid: false,
		reason: "unknown",
		message: `Token validation failed: ${e}`
	};
	return {
		valid: false,
		reason: "invalid_token",
		message: "Token is invalid or expired."
	};
};
const validateScopes = (scopes) => {
	if (REQUIRED_SCOPES.some((scope) => scopes.includes(scope))) return void 0;
	return {
		valid: false,
		reason: "invalid_scopes",
		message: `Token has scope(s) ${scopes.map((scope) => `'${scope}'`).join(", ")} (needs one of: ${REQUIRED_SCOPES.join(", ")}).`
	};
};
async function checkPatToken(simpleClient, token) {
	const { scopes } = await simpleClient.fetchFromApi({
		type: "rest",
		method: "GET",
		path: "/api/v4/personal_access_tokens/self"
	});
	const scopeValidationError = validateScopes(scopes);
	if (scopeValidationError) return scopeValidationError;
	return {
		valid: true,
		tokenInfo: {
			scopes,
			type: "pat",
			token
		}
	};
}
async function checkOAuthToken(simpleClient, token) {
	const { scope: scopes } = await simpleClient.fetchFromApi({
		type: "rest",
		method: "GET",
		path: "/oauth/token/info"
	});
	const scopeValidationError = validateScopes(scopes);
	if (scopeValidationError) return scopeValidationError;
	return {
		valid: true,
		tokenInfo: {
			scopes,
			type: "oauth",
			token
		}
	};
}
async function checkToken(simpleClient, token, tokenType) {
	if (!token) return {
		valid: false,
		message: "No token provided",
		reason: "invalid_token"
	};
	if (tokenType === "pat") return checkPatToken(simpleClient, token).catch(handleTokenCheckError);
	if (tokenType === "oauth") return checkOAuthToken(simpleClient, token).catch(handleTokenCheckError);
	const responses = await Promise.all([checkPatToken(simpleClient, token).catch(handleTokenCheckError), checkOAuthToken(simpleClient, token).catch(handleTokenCheckError)]);
	const validResponse = responses.find((r) => r.valid);
	if (validResponse) return validResponse;
	const invalidResponses = responses.filter((r) => !r.valid);
	const invalidScopeError = invalidResponses.find((r) => r.reason === "invalid_scopes");
	if (invalidScopeError) return invalidScopeError;
	const invalidTokenError = invalidResponses.find((r) => r.reason === "invalid_token");
	if (invalidTokenError) return invalidTokenError;
	return responses[0];
}
//#endregion
//#region src/gitlab_project/gitlab_project_service.ts
let _initClass$2;
function _applyDecs$2(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$2(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$2(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$2(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$2(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$2(t) {
	var i = _toPrimitive$2(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$2(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$2(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$2(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const ProjectService = createInterfaceId("ProjectService");
let _DefaultProjectServic;
(class {
	static {
		[_DefaultProjectServic, _initClass$2] = _applyDecs$2(this, [Injectable(ProjectService, [Logger, GitLabApiService])], []).c;
	}
	#gitlabApiService;
	#logger;
	constructor(logger, gitlabApi) {
		this.#gitlabApiService = gitlabApi;
		this.#logger = withPrefix(logger, "[ProjectService]");
	}
	async getProjectFromPathWithNamespace(projectPathWithNamespace) {
		this.#logger.debug(`Fetching details for project: ${projectPathWithNamespace}`);
		return (await this.#gitlabApiService.fetchFromApi({
			type: "graphql",
			query: gql`
        query getProjectDetails($fullPath: ID!) {
          project(fullPath: $fullPath) {
            id
            namespace {
              id
              rootNamespace {
                id
              }
            }
          }
        }
      `,
			variables: { fullPath: projectPathWithNamespace }
		})).project;
	}
	/**
	* Fetches project IDs from GitLab API using project full paths.
	* @param projectPaths Array of project full paths (e.g., ['gitlab-org/gitlab'])
	* @returns Array of projects with their GitLab IDs and full paths
	*/
	async getProjectIdsFromPaths(projectPaths) {
		return (await this.#gitlabApiService.fetchFromApi({
			type: "graphql",
			query: gql`
        query getProjectIds($fullPaths: [String!]!) {
          projects(fullPaths: $fullPaths) {
            nodes {
              id
              fullPath
            }
          }
        }
      `,
			variables: { fullPaths: projectPaths }
		})).projects.nodes;
	}
	/**
	* Checks which projects are duo eligible and returns only those projects.
	* @param projectIds Array of GitLab project IDs
	* @returns Array of projects that are duo eligible
	*/
	async filterToProjectsWithDuoEligible(projectIds) {
		try {
			return (await this.#gitlabApiService.fetchFromApi({
				type: "graphql",
				query: gql`
          query getDuoEligibleProjects($ids: [ID!]!, $withDuoEligible: Boolean!) {
            projects(ids: $ids, withDuoEligible: $withDuoEligible) {
              nodes {
                id
                fullPath
              }
            }
          }
        `,
				variables: {
					ids: projectIds,
					withDuoEligible: true
				}
			})).projects.nodes;
		} catch (error) {
			this.#logger.debug("Failed to filter duo eligible projects, returning empty array", error);
			return [];
		}
	}
	static {
		_initClass$2();
	}
});
//#endregion
//#region src/gitlab_user/get_user_avatar_url.ts
const getUserAvatarUrl = (avatarUrl, webUrl) => {
	if (!avatarUrl) return "";
	if (avatarUrl.startsWith("http://") || avatarUrl.startsWith("https://") || avatarUrl.startsWith("data:image/")) return avatarUrl;
	try {
		return `${new URL(webUrl).origin}${avatarUrl}`;
	} catch {
		return "";
	}
};
//#endregion
//#region src/gitlab_user/default_user_service.ts
let _initClass$1;
function _applyDecs$1(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$1(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$1(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$1(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$1(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$1(t) {
	var i = _toPrimitive$1(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$1(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$1(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$1(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const UserService = createInterfaceId("UserService");
const getCurrentUserQuery = {
	type: "graphql",
	query: gql`
    query getUser {
      currentUser {
        id
        username
        name
        avatarUrl
        webUrl
        userPreferences @gl_introduced(version: "18.10.0") {
          duoDefaultNamespace {
            id
            fullName
            fullPath
          }
        }
      }
    }
  `,
	variables: {}
};
let _DefaultUserService;
(class {
	static {
		[_DefaultUserService, _initClass$1] = _applyDecs$1(this, [Injectable(UserService, [Logger, GitLabApiService])], []).c;
	}
	#apiService;
	#logger;
	#subscriptions = [];
	#userPromise;
	#userResolve;
	#userReject;
	constructor(logger, apiService) {
		this.#apiService = apiService;
		this.#logger = withPrefix(logger, "[UserService]");
		this.#createUserPromise();
		this.#subscriptions.push(this.#apiService.onApiReconfigured(async (data) => {
			if (!data.isInValidState) {
				this.user = void 0;
				this.#createUserPromise();
				return;
			}
			try {
				const currentUser = (await this.#apiService.fetchFromApi(getCurrentUserQuery)).currentUser;
				const id = currentUser.id;
				const username = currentUser.username;
				const name = currentUser.name;
				const avatarUrlOrig = currentUser.avatarUrl;
				const webUrl = currentUser.webUrl;
				const avatarUrl = getUserAvatarUrl(avatarUrlOrig, webUrl);
				const restId = tryParseGitLabGid(id);
				if (!restId) throw new Error(`Failed to parse user GID into REST ID: "${id}"`);
				this.user = {
					id,
					restId,
					username,
					name,
					avatarUrl,
					duoDefaultNamespacePath: currentUser.userPreferences?.duoDefaultNamespace?.fullPath,
					duoDefaultNamespaceId: tryParseGitLabGidToString(currentUser.userPreferences?.duoDefaultNamespace?.id) || void 0
				};
				this.#logger.debug(`New user fetched: ${JSON.stringify(this.user)}`);
				this.#userResolve?.(this.user);
			} catch (e) {
				this.#logger.error("Failed to get user from API", e);
				this.user = void 0;
				this.#userReject?.(e);
			}
		}));
	}
	async getUser() {
		if (this.user) return this.user;
		return this.#userPromise;
	}
	#createUserPromise() {
		this.#userPromise = new Promise((resolve, reject) => {
			this.#userResolve = resolve;
			this.#userReject = reject;
		});
	}
	dispose = () => this.#subscriptions.forEach((d) => d.dispose());
	static {
		_initClass$1();
	}
});
//#endregion
//#region src/circuit_breaker/circuit_breaker.ts
const CIRCUIT_BREAK_INTERVAL_MS = 1e4;
const MAX_ERRORS_BEFORE_CIRCUIT_BREAK = 4;
let CircuitBreakerState = /* @__PURE__ */ function(CircuitBreakerState) {
	CircuitBreakerState["OPEN"] = "Open";
	CircuitBreakerState["CLOSED"] = "Closed";
	return CircuitBreakerState;
}({});
//#endregion
//#region src/circuit_breaker/fixed_time_circuit_breaker.ts
var FixedTimeCircuitBreaker = class {
	#state = CircuitBreakerState.CLOSED;
	#maxErrorsBeforeBreaking;
	#breakTimeMs;
	#errorCount = 0;
	#eventEmitter = new EventEmitter();
	#timeoutId = null;
	/**
	* @param name identifier of the circuit breaker that will appear in the logs
	* */
	constructor(maxErrorsBeforeBreaking = 4, breakTimeMs = CIRCUIT_BREAK_INTERVAL_MS) {
		this.#maxErrorsBeforeBreaking = maxErrorsBeforeBreaking;
		this.#breakTimeMs = breakTimeMs;
	}
	error() {
		this.#errorCount += 1;
		if (this.#errorCount >= this.#maxErrorsBeforeBreaking) this.#open();
	}
	#open() {
		this.#state = CircuitBreakerState.OPEN;
		this.#timeoutId = setTimeout(() => this.#close(), this.#breakTimeMs);
		this.#eventEmitter.emit("open");
	}
	isOpen() {
		return this.#state === CircuitBreakerState.OPEN;
	}
	#close() {
		if (this.#state === CircuitBreakerState.CLOSED) return;
		if (this.#timeoutId) clearTimeout(this.#timeoutId);
		this.#state = CircuitBreakerState.CLOSED;
		this.#eventEmitter.emit("close");
	}
	success() {
		this.#errorCount = 0;
		this.#close();
	}
	onOpen(listener) {
		this.#eventEmitter.on("open", listener);
		return { dispose: () => this.#eventEmitter.removeListener("open", listener) };
	}
	onClose(listener) {
		this.#eventEmitter.on("close", listener);
		return { dispose: () => this.#eventEmitter.removeListener("close", listener) };
	}
};
//#endregion
//#region src/circuit_breaker/exponential_backoff_circuit_breaker.ts
const DEFAULT_INITIAL_BACKOFF_MS = 1e3;
const DEFAULT_MAX_BACKOFF_MS = 6e4;
const DEFAULT_BACKOFF_MULTIPLIER = 2;
var ExponentialBackoffCircuitBreaker = class {
	#state = CircuitBreakerState.CLOSED;
	#initialBackoffMs;
	#maxBackoffMs;
	#backoffMultiplier;
	#errorCount = 0;
	#eventEmitter = new EventEmitter();
	#timeoutId = null;
	constructor({ initialBackoffMs = DEFAULT_INITIAL_BACKOFF_MS, maxBackoffMs = DEFAULT_MAX_BACKOFF_MS, backoffMultiplier = 2 } = {}) {
		this.#initialBackoffMs = initialBackoffMs;
		this.#maxBackoffMs = maxBackoffMs;
		this.#backoffMultiplier = backoffMultiplier;
	}
	error() {
		this.#errorCount += 1;
		this.#open();
	}
	megaError() {
		this.#errorCount += 3;
		this.#open();
	}
	#open() {
		if (this.#state === CircuitBreakerState.OPEN) return;
		this.#state = CircuitBreakerState.OPEN;
		this.#timeoutId = setTimeout(() => this.#close(), this.#getBackoffTime());
		this.#eventEmitter.emit("open");
	}
	#close() {
		if (this.#state === CircuitBreakerState.CLOSED) return;
		if (this.#timeoutId) clearTimeout(this.#timeoutId);
		this.#state = CircuitBreakerState.CLOSED;
		this.#eventEmitter.emit("close");
	}
	isOpen() {
		return this.#state === CircuitBreakerState.OPEN;
	}
	success() {
		this.#errorCount = 0;
		this.#close();
	}
	onOpen(listener) {
		this.#eventEmitter.on("open", listener);
		return { dispose: () => this.#eventEmitter.removeListener("open", listener) };
	}
	onClose(listener) {
		this.#eventEmitter.on("close", listener);
		return { dispose: () => this.#eventEmitter.removeListener("close", listener) };
	}
	#getBackoffTime() {
		return Math.min(this.#initialBackoffMs * this.#backoffMultiplier ** (this.#errorCount - 1), this.#maxBackoffMs);
	}
};
//#endregion
//#region src/event_emitter/event_emitter.ts
/** This interface exactly copies the vscode.EventEmitter class */
/**
* This is an arbitrary name. The node event emitter supports multiple
* types of events per emitter but we need only one, so we hardcode it.
*/
const EVENT_NAME = "EVENT";
/**
* This is a  simplified implementation of the vscode.EventEmitter.
*/
var EventEmitterImpl = class {
	#abortController;
	eventEmitter = new EventEmitter().setMaxListeners(100);
	event = (listener, thisArgs = {}) => {
		const nodeListener = (e, signal) => listener.bind(thisArgs)(e, signal);
		this.eventEmitter.on(EVENT_NAME, nodeListener);
		return { dispose: () => this.eventEmitter.removeListener(EVENT_NAME, nodeListener) };
	};
	fire(data) {
		if (this.#abortController) this.#abortController.abort();
		this.#abortController = new AbortController();
		this.eventEmitter.emit(EVENT_NAME, data, this.#abortController.signal);
	}
	dispose() {
		this.#abortController?.abort();
		this.eventEmitter.removeAllListeners();
	}
};
//#endregion
//#region src/circuit_breaker/max_attempts_circuit_breaker.ts
/**
* A circuit breaker that trips after a maximum number of attempts.
*/
var MaxAttemptsCircuitBreaker = class {
	#maxAttempts;
	#attempts = 0;
	#innerBreaker;
	#maxAttemptsEventEmitter = new EventEmitterImpl();
	constructor(circuitBreaker, maxAttempts) {
		this.#maxAttempts = maxAttempts;
		this.#innerBreaker = circuitBreaker;
	}
	error() {
		this.#attempts += 1;
		if (this.#reachedMaxAttempts) {
			this.#maxAttemptsEventEmitter.fire();
			return;
		}
		this.#innerBreaker.error();
	}
	success() {
		this.#attempts = 0;
		this.#innerBreaker.success();
	}
	onOpen(listener) {
		return this.#innerBreaker.onOpen(listener);
	}
	onClose(listener) {
		return this.#innerBreaker.onClose(listener);
	}
	get #reachedMaxAttempts() {
		return this.#attempts >= this.#maxAttempts;
	}
	onReachedMaxAttempts = this.#maxAttemptsEventEmitter.event;
	isOpen() {
		return this.#reachedMaxAttempts || this.#innerBreaker.isOpen();
	}
	/**
	* Resets the circuit breaker to its initial state.
	*/
	reset() {
		this.#attempts = 0;
		this.#innerBreaker.success();
	}
};
//#endregion
//#region src/utils/if_version_gte.ts
/**
* This method runs different code for different versions (VS Code, GitLab API, ...).
*
* - If the method can't parse the `current` version, it will execute the **then** part.
* - If the method can't parse the `minimumRequiredVersion`, it will throw an assertion error.
*
* @param current the version this extension uses
* @param minimumRequiredVersion the version where `then` code block can execute safely
* @param then code to be executed on the the minimumRequiredVersion or higher
* @param otherwise code to be executed on lower versions
* @returns
*/
function ifVersionGte(current, minimumRequiredVersion, then, otherwise) {
	if (!valid(minimumRequiredVersion)) throw new Error(`minimumRequiredVersion argument ${minimumRequiredVersion} isn't valid`);
	const parsedCurrent = coerce(current);
	if (!parsedCurrent) {
		console.warn(`Could not parse version from "${current}", running logic for the latest GitLab version`);
		return then();
	}
	if (gte(parsedCurrent, minimumRequiredVersion)) return then();
	return otherwise();
}
//#endregion
//#region src/utils/async_debounce.ts
/**
* Creates a debounced version of an asynchronous function. A single promise is returned, which
* resolves to the result of the latest fn call when the wait time expires.
*
* @template TFunc - The type of the asynchronous function to debounce.
* @param {TFunc} func - The asynchronous function to debounce.
* @param {number} wait - The number of milliseconds to delay.
* @returns {AsyncDebouncedFunction<TFunc>} A new debounced function.
*/
function asyncDebounce(func, wait) {
	const resolveSet = /* @__PURE__ */ new Set();
	const rejectSet = /* @__PURE__ */ new Set();
	const debounced = debounce(function debouncedFn(...args) {
		func.apply(this, args).then((result) => {
			resolveSet.forEach((resolve) => resolve(result));
			resolveSet.clear();
			rejectSet.clear();
			return result;
		}).catch((error) => {
			rejectSet.forEach((reject) => reject(error));
			resolveSet.clear();
			rejectSet.clear();
		});
	}, wait);
	return function debouncedWrapper(...args) {
		return new Promise((resolve, reject) => {
			resolveSet.add(resolve);
			rejectSet.add(reject);
			debounced.apply(this, args);
		});
	};
}
//#endregion
//#region src/utils/byte_size.ts
function getByteSize(str) {
	return Buffer.from(str).byteLength;
}
/**
* Truncates a string to a specified byte limit, attempting to break at word boundaries.
* If no word boundary is found, truncates at the byte limit.
*
* @param str - The string to truncate
* @param byteLimit - Maximum number of bytes allowed (including suffix if present)
* @param options - Configuration options
* @param options.separator - RegExp pattern to identify word boundaries (defaults to whitespace)
* @param options.suffix - Text to append when truncation occurs
* @returns The truncated string with optional suffix if truncation occurred
*/
function truncateToByteLimit(str, byteLimit, options = {}) {
	const { separator = /\s+/g, suffix } = options;
	if (getByteSize(str) <= byteLimit) return str;
	const suffixBytes = suffix ? getByteSize(` ${suffix}`) : 0;
	const availableBytes = Math.max(0, byteLimit - suffixBytes);
	if (availableBytes === 0 && suffix) return ` ${suffix}`.slice(0, byteLimit);
	let truncated = Buffer.from(str).subarray(0, availableBytes).toString();
	const lastSeparatorMatch = [...truncated.matchAll(separator)].pop();
	if (lastSeparatorMatch) truncated = truncated.slice(0, lastSeparatorMatch.index);
	return suffix ? `${truncated} ${suffix}` : truncated;
}
//#endregion
//#region src/utils/do_not_await.ts
/** doNotAwait is used to circumvent the otherwise invaluable
* @typescript-eslint/no-floating-promises rule. This util is meant
* for informative messages that would otherwise block execution */
const doNotAwait = (_promise) => {};
//#endregion
//#region src/utils/gid_utils.ts
const GID_NAMESPACE_GROUP = "Group";
const GID_NAMESPACE_ISSUE = "Issue";
const GID_NAMESPACE_MERGE_REQUEST = "MergeRequest";
const GID_NAMESPACE_PROJECT = "Project";
/**
* Creates a GitLab global ID / GraphQL ID, from a namespace and standalone ID / REST ID
*/
function toGitLabGid(namespace, id) {
	return `gid://gitlab/${namespace}/${id}`;
}
/**
* Try to parse a GitLab global ID / GraphQL ID into the ID part / REST ID
*/
function tryParseGitLabGid(gid) {
	const result = parseInt(gid.replace(/gid:\/\/gitlab\/.*\//g, ""), 10);
	if (!result || Number.isNaN(result)) return;
	return result;
}
/**
* Try to parse a GitLab global ID / GraphQL ID into a string ID, returning empty string if parsing fails
*/
function tryParseGitLabGidToString(gid) {
	if (!gid) return "";
	return (tryParseGitLabGid(gid) ?? "").toString();
}
//#endregion
//#region src/utils/diff_emitter.ts
/** promotes EventEmitter into an emitter that will only fire change event if the value T has changed
* @param emitter the base emitter this function wraps
* @param equalFn optionally, you can provide a function to compare the event data, if no equalFn is provided, we use lodash isEqual
*/
const diffEmitter = (emitter, equalFn) => {
	let previousValue;
	const isEqualFn = equalFn || isEqual;
	return {
		event: emitter.event.bind(emitter),
		fire: (data) => {
			if (!isEqualFn(previousValue, data)) {
				previousValue = cloneDeep(data);
				emitter.fire(data);
			}
		},
		dispose: emitter.dispose.bind(emitter)
	};
};
//#endregion
//#region src/utils/object_key_transforms.ts
/**
* Recursively transforms all object keys from snake_case to camelCase.
* Arrays are processed recursively. Non-object values are returned as-is.
*
* @param obj - The object, array, or primitive value to transform
* @returns A new object/array with transformed keys, or the original value for primitives
*/
function deepCamelCaseKeys(obj) {
	if (isPlainObject(obj)) {
		const result = {};
		const typedObj = obj;
		Object.keys(typedObj).forEach((key) => {
			result[camelCase(key)] = deepCamelCaseKeys(typedObj[key]);
		});
		return result;
	}
	if (Array.isArray(obj)) return obj.map((item) => deepCamelCaseKeys(item));
	return obj;
}
/**
* Recursively transforms all object keys from camelCase to snake_case.
* Arrays are processed recursively. Non-object values are returned as-is.
*
* @param obj - The object, array, or primitive value to transform
* @returns A new object/array with transformed keys, or the original value for primitives
*/
function deepSnakeCaseKeys(obj) {
	if (isPlainObject(obj)) {
		const result = {};
		const typedObj = obj;
		Object.keys(typedObj).forEach((key) => {
			result[snakeCase(key)] = deepSnakeCaseKeys(typedObj[key]);
		});
		return result;
	}
	if (Array.isArray(obj)) return obj.map((item) => deepSnakeCaseKeys(item));
	return obj;
}
//#endregion
//#region src/utils/try_parse_url.ts
const tryParseUrl = (url) => {
	try {
		return new URL(url);
	} catch {
		return;
	}
};
//#endregion
//#region src/utils/format_time_ago.ts
const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
const DIVISIONS = [
	{
		amount: 60,
		name: "second",
		abbr: "s"
	},
	{
		amount: 60,
		name: "minute",
		abbr: "m"
	},
	{
		amount: 24,
		name: "hour",
		abbr: "h"
	},
	{
		amount: 7,
		name: "day",
		abbr: "d"
	},
	{
		amount: 4.34524,
		name: "week",
		abbr: "w"
	},
	{
		amount: 12,
		name: "month",
		abbr: "mo"
	},
	{
		amount: Number.POSITIVE_INFINITY,
		name: "year",
		abbr: "y"
	}
];
/**
* @param date ISO string to format
* @param useAbbreviations Whether to use abbreviated time units
* @returns Formatted date or original date if an error occurs.
*/
function formatTimeAgo(date, useAbbreviations = false) {
	const dateToFormat = new Date(date);
	if (Number.isNaN(dateToFormat.getTime())) return date;
	let duration = (dateToFormat.getTime() - Date.now()) / 1e3;
	for (const division of DIVISIONS) {
		if (Math.abs(duration) < division.amount) {
			if (useAbbreviations) {
				const value = Math.round(duration);
				const unit = division.abbr;
				return `${duration > 0 ? "in " : ""}${Math.abs(value)}${unit}${duration < 0 ? " ago" : ""}`;
			}
			return formatter.format(Math.round(duration), division.name).replace(/last/, "Last");
		}
		duration /= division.amount;
	}
	return date;
}
//#endregion
//#region src/utils/with_timeout.ts
/**
* Races a promise against a timeout. Rejects with an error if the
* timeout elapses before the promise settles. The timer is always
* cleaned up regardless of the outcome.
*/
async function withTimeout(promise, ms) {
	let timer;
	try {
		return await Promise.race([promise, new Promise((_, reject) => {
			timer = setTimeout(() => reject(/* @__PURE__ */ new Error(`Timed out after ${ms}ms`)), ms);
		})]);
	} finally {
		clearTimeout(timer);
	}
}
//#endregion
//#region src/utils/runtime.ts
/**
* Returns true when running under the Bun runtime.
*
* Use this to switch behaviour that depends on runtime-specific APIs, i.e.
* where Bun and Node genuinely differ and the difference cannot be papered
* over. Prefer a runtime-independent implementation where one exists: the
* WebSocket transport in `@gitlab-org/fetch`, for example, used to branch on
* this and now runs the same pure-JS `ws` client everywhere.
*
* Safe in the browser bundle, where `process.versions` is undefined.
*/
function isBunRuntime() {
	return typeof process !== "undefined" && typeof process.versions !== "undefined" && typeof process.versions.bun === "string";
}
//#endregion
//#region src/utils/eol_utils.ts
/**
* Detects the dominant end-of-line sequence in the given content.
*
* Returns `\r\n` when CRLF line endings are the majority, otherwise `\n`.
* Files with no line endings (or a tie) default to `\n`.
*
* Note: in a mixed-EOL file, callers that use `applyEol` to re-encode strings
* for matching/writing will normalize toward the detected majority — minority
* EOLs in the replacement region are silently converted on write.
*/
function detectEol(content) {
	const crlfCount = (content.match(/\r\n/g) ?? []).length;
	return crlfCount > (content.match(/\n/g) ?? []).length - crlfCount ? "\r\n" : "\n";
}
/**
* Normalizes all line endings (`\r\n` and lone `\r`) in the given text to `\n`.
*/
function normalizeToLf(text) {
	return text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}
/**
* Re-encodes text to use the given end-of-line sequence.
*
* The input is first normalized to `\n` so the result has consistent line
* endings regardless of the input's original mix.
*
* Use this when matching or writing model-supplied strings against file
* content: the model frequently emits LF even when the target file is CRLF,
* so re-encoding the needle to the file's dominant EOL makes `includes` /
* `split` succeed and preserves the file's line endings on write.
*/
function applyEol(text, eol) {
	const normalized = normalizeToLf(text);
	return eol === "\r\n" ? normalized.replace(/\n/g, "\r\n") : normalized;
}
/**
* Splits text into lines tolerant of any line-ending style (`\r\n`, `\r`, or `\n`).
*
* Use this when parsing text that may originate from CRLF sources (e.g. unified
* diffs generated from CRLF files on Windows) so that line content does not
* carry a trailing carriage return.
*/
function splitLines(text) {
	return text.split(/\r\n|\r|\n/);
}
//#endregion
//#region src/feature_flags/constants.ts
let InstanceFeatureFlags = /* @__PURE__ */ function(InstanceFeatureFlags) {
	InstanceFeatureFlags["EditorAdvancedContext"] = "advanced_context_resolver";
	InstanceFeatureFlags["CodeSuggestionsContext"] = "code_suggestions_context";
	InstanceFeatureFlags["UseDuoContextExclusion"] = "use_duo_context_exclusion";
	InstanceFeatureFlags["DuoWorkflow"] = "duo_workflow";
	InstanceFeatureFlags["UserModelSwitching"] = "ai_user_model_switching";
	InstanceFeatureFlags["SoftwareDevelopmentFlowRegistry"] = "software_development_flow_registry";
	return InstanceFeatureFlags;
}({});
const InstanceFeatureFlagIntroduced = {
	[InstanceFeatureFlags.DuoWorkflow]: "17.2.0",
	[InstanceFeatureFlags.EditorAdvancedContext]: "17.1.0",
	[InstanceFeatureFlags.CodeSuggestionsContext]: "17.1.0",
	[InstanceFeatureFlags.UseDuoContextExclusion]: "18.2.0",
	[InstanceFeatureFlags.UserModelSwitching]: "18.4.0",
	[InstanceFeatureFlags.SoftwareDevelopmentFlowRegistry]: "19.1.0"
};
const InstanceFeatureFlagRollout = {
	[InstanceFeatureFlags.DuoWorkflow]: "18.2.0",
	[InstanceFeatureFlags.EditorAdvancedContext]: "17.4.0",
	[InstanceFeatureFlags.CodeSuggestionsContext]: "17.4.0",
	[InstanceFeatureFlags.UseDuoContextExclusion]: "18.5.0",
	[InstanceFeatureFlags.UserModelSwitching]: "18.7.0",
	[InstanceFeatureFlags.SoftwareDevelopmentFlowRegistry]: "19.2.0"
};
//#endregion
//#region src/feature_flags/instance_feature_flags.ts
let _initClass;
function _applyDecs(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey(t) {
	var i = _toPrimitive(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const queryGetInstanceFlags = gql`
  query featureFlagsEnabled($names: [String!]!) {
    metadata {
      featureFlags(names: $names) {
        enabled
        name
      }
    }
  }
`;
const queryGetInstanceFlagLegacy = gql`
  query featureFlagEnabled($name: String!) {
    featureFlagEnabled(name: $name)
  }
`;
const getInstanceFeatureFlagsRequest = (featureNames) => {
	return {
		type: "graphql",
		query: queryGetInstanceFlags,
		variables: { names: featureNames }
	};
};
const InstanceFeatureFlagsService = createInterfaceId("InstanceFeatureFlagsService");
let _DefaultInstanceFeatu;
(class {
	static {
		[_DefaultInstanceFeatu, _initClass] = _applyDecs(this, [Injectable(InstanceFeatureFlagsService, [Logger, GitLabApiService])], []).c;
	}
	#api;
	#logger;
	#featureFlags = /* @__PURE__ */ new Map();
	#eventEmitter = diffEmitter(new EventEmitterImpl());
	#throttledUpdateInstanceFeatureFlags;
	constructor(logger, api) {
		this.#api = api;
		this.#featureFlags = /* @__PURE__ */ new Map();
		this.#logger = withPrefix(logger, "[CoreInstanceFeatureFlagService]");
		this.#throttledUpdateInstanceFeatureFlags = throttle(this.updateInstanceFeatureFlags.bind(this), 5e3);
		this.#api.onApiReconfigured(async ({ isInValidState }) => {
			if (!isInValidState) return;
			await this.#updateInstanceFeatureFlags();
		});
	}
	/**
	* Fetches the feature flags from the gitlab instance
	* and updates the internal state.
	* This function is throttled to prevent network thrashing if called rapidly.
	*/
	updateInstanceFeatureFlags() {
		return this.#throttledUpdateInstanceFeatureFlags() ?? this.#updateInstanceFeatureFlags();
	}
	/**
	* Fetches a single feature flag using the old query (for instances < 17.4)
	*/
	async #fetchSingleFeatureFlag(name) {
		try {
			const result = await this.#api.fetchFromApi({
				type: "graphql",
				query: queryGetInstanceFlagLegacy,
				variables: { name }
			});
			this.#logger.debug(`feature flag ${name} is ${result.featureFlagEnabled}`);
			return result.featureFlagEnabled;
		} catch (e) {
			if (e instanceof ClientError) if (e.message.match(/field '([^']+)' doesn't exist on type/i)) this.#logger.debug(`query doesn't exist`, e.message);
			else this.#logger.error(`error fetching feature flag ${name}`, e);
			return;
		}
	}
	/**
	* Apply default logic for feature flags based on the instance version
	*/
	#applyFeatureFlagDefaults(flags, fetchedFlags, version) {
		return Object.fromEntries(flags.map((flag) => {
			if (fetchedFlags[flag] !== void 0) return [flag, fetchedFlags[flag]];
			return ifVersionGte(version, InstanceFeatureFlagIntroduced[flag], () => {
				const defaultEnabled = InstanceFeatureFlagRollout[flag];
				return [flag, defaultEnabled ? ifVersionGte(version, defaultEnabled, () => true, () => false) : false];
			}, () => [flag, false]);
		}));
	}
	/**
	* Fetch feature flags using the batch query (17.4+)
	*/
	async #fetchInstanceFeatureFlagsCurrent(flags) {
		const response = await this.#api.fetchFromApi(getInstanceFeatureFlagsRequest(flags));
		if (!response.metadata) return {};
		const result = {};
		for (const flag of flags) {
			const instanceFlag = response.metadata.featureFlags.find(({ name }) => name === flag);
			if (instanceFlag?.enabled !== void 0) result[flag] = instanceFlag.enabled;
		}
		return result;
	}
	/**
	* Fallback method for fetching feature flags on instances < 17.4
	*/
	async #fetchInstanceFeatureFlagsLegacy(flags) {
		const result = {};
		for (const flag of flags) {
			const enabled = await this.#fetchSingleFeatureFlag(flag);
			if (enabled !== void 0) result[flag] = enabled;
		}
		return result;
	}
	async #fetchInstanceFeatureFlags(flags) {
		try {
			const { version } = await this.#api.fetchFromApi(versionRequest);
			const fetchedFlags = await ifVersionGte(version, "17.4.0", () => this.#fetchInstanceFeatureFlagsCurrent(flags), () => this.#fetchInstanceFeatureFlagsLegacy(flags));
			return this.#applyFeatureFlagDefaults(flags, fetchedFlags, version);
		} catch (e) {
			this.#logger.error(e);
			return {};
		}
	}
	async #updateInstanceFeatureFlags() {
		this.#logger.debug("Populating feature flags");
		const flags = await this.#fetchInstanceFeatureFlags(Object.values(InstanceFeatureFlags));
		for (const [flag, enabled] of Object.entries(flags)) {
			this.#logger.debug(`Instance feature flag "${flag}" is ${enabled ? "enabled" : "disabled"}`);
			this.#featureFlags.set(flag, enabled);
		}
		this.#eventEmitter.fire(new Map(this.#featureFlags));
	}
	/**
	* Checks if a feature flag is enabled on the GitLab instance.
	* @see `GitLabApiClient` for how the instance is determined.
	* @requires `updateInstanceFeatureFlags` to be called first.
	*/
	isInstanceFlagEnabled(name) {
		return this.#featureFlags.get(name) ?? false;
	}
	onChanged = (listener) => {
		const disposable = this.#eventEmitter.event(listener);
		listener(new Map(this.#featureFlags));
		return disposable;
	};
	static {
		_initClass();
	}
});
//#endregion
//#region src/feature_flags/feature_flags.ts
let ClientFeatureFlags = /* @__PURE__ */ function(ClientFeatureFlags) {
	ClientFeatureFlags["StreamCodeGenerations"] = "streamCodeGenerations";
	ClientFeatureFlags["DuoWorkflow"] = "duoWorkflow";
	ClientFeatureFlags["RemoteSecurityScans"] = "remoteSecurityScans";
	ClientFeatureFlags["EditFileDiagnosticsResponse"] = "editFileDiagnosticsResponse";
	return ClientFeatureFlags;
}({});
const FeatureFlagService = createInterfaceId("FeatureFlagService");
//#endregion
//#region src/pending_promise/create_pending_promise.ts
var DefaultPendingPromise = class {
	#promise = Promise.resolve(0);
	#resolver;
	#rejector;
	constructor() {
		this.#initializePromise();
	}
	resolve(value) {
		this.#resolver?.(value);
		this.#initializePromise();
	}
	reject(reason) {
		this.#rejector?.(reason);
		this.#initializePromise();
	}
	get promise() {
		return this.#promise;
	}
	#initializePromise() {
		this.#promise = new Promise((resolve, reject) => {
			this.#resolver = resolve;
			this.#rejector = reject;
		});
	}
};
const createPendingPromise = () => {
	return new DefaultPendingPromise();
};
//#endregion
//#region src/feature_state_management/feature_state_manager.ts
const FeatureStateManager = createInterfaceId("FeatureStateManager");
//#endregion
//#region src/feature_state_management/types.ts
const AUTHENTICATION = "authentication";
const CODE_SUGGESTIONS = "code_suggestions";
const CHAT = "chat";
const CHAT_TERMINAL_CONTEXT = "chat_terminal_context";
const AGENTIC_CHAT = "agentic_chat";
const AGENT_PLATFORM = "agent_platform";
const FLOWS = "flows";
const SANDBOX = "sandbox";
const AUTHENTICATION_REQUIRED = "authentication-required";
const INVALID_TOKEN = "invalid-token";
const SUGGESTIONS_AUTHENTICATION_REQUIRED = "code-suggestions-authentication-required";
const SUGGESTIONS_NO_LICENSE = "code-suggestions-no-license";
const CHAT_AUTHENTICATION_REQUIRED = "chat-authentication-required";
const CHAT_NO_LICENSE = "chat-no-license";
const DUO_DISABLED_FOR_PROJECT = "duo-disabled-for-project";
const UNSUPPORTED_GITLAB_VERSION = "code-suggestions-unsupported-gitlab-version";
const UNSUPPORTED_LANGUAGE = "code-suggestions-document-unsupported-language";
const DISABLED_LANGUAGE = "code-suggestions-document-disabled-language";
const SUGGESTIONS_API_ERROR = "code-suggestions-api-error";
const SUGGESTIONS_NO_DEFAULT_NAMESPACE = "code-suggestions-no-default-namespace";
const SUGGESTIONS_NO_CREDITS = "code-suggestions-no-credits";
const CHAT_DISABLED_BY_USER = "chat-disabled-by-user";
const SUGGESTIONS_DISABLED_BY_USER = "code-suggestions-disabled-by-user";
const SUGGESTIONS_FILE_EXCLUDED = "code-suggestions-file-excluded";
const CHAT_INCLUDE_TERMINAL_CONTEXT_UNAVAILABLE = "chat-include-terminal-context-unavailable";
const AGENT_PLATFORM_DISABLED_BY_USER = "agent-platform-disabled-by-user";
const AGENTIC_CHAT_NO_SUPPORT = "agentic-chat-no-support";
const CLASSIC_CHAT_NO_LICENSE = "classic-chat-no-license";
const FLOWS_INSTANCE_FLAG_DISABLED = "flows-instance-flag-disabled";
const SANDBOX_DISABLED_BY_USER = "sandbox-disabled-by-user";
const SANDBOX_UNSUPPORTED_PLATFORM = "sandbox-unsupported-platform";
const SANDBOX_MISSING_DEPENDENCIES = "sandbox-missing-dependencies";
const AUTHENTICATION_CHECK_PRIORITY_ORDERED = [AUTHENTICATION_REQUIRED];
const CODE_SUGGESTIONS_CHECKS_PRIORITY_ORDERED = [
	...AUTHENTICATION_CHECK_PRIORITY_ORDERED,
	SUGGESTIONS_DISABLED_BY_USER,
	SUGGESTIONS_API_ERROR,
	UNSUPPORTED_GITLAB_VERSION,
	SUGGESTIONS_NO_LICENSE,
	SUGGESTIONS_NO_DEFAULT_NAMESPACE,
	SUGGESTIONS_NO_CREDITS,
	DUO_DISABLED_FOR_PROJECT,
	SUGGESTIONS_FILE_EXCLUDED,
	UNSUPPORTED_LANGUAGE,
	DISABLED_LANGUAGE
];
const CHAT_CHECKS_PRIORITY_ORDERED = [
	...AUTHENTICATION_CHECK_PRIORITY_ORDERED,
	CHAT_DISABLED_BY_USER,
	CLASSIC_CHAT_NO_LICENSE,
	CHAT_NO_LICENSE,
	DUO_DISABLED_FOR_PROJECT
];
const AGENTIC_CHAT_CHECKS_PRIORITY_ORDERED = [...AUTHENTICATION_CHECK_PRIORITY_ORDERED, AGENTIC_CHAT_NO_SUPPORT];
const FLOWS_CHECKS_PRIORITY_ORDERED = [...AUTHENTICATION_CHECK_PRIORITY_ORDERED, FLOWS_INSTANCE_FLAG_DISABLED];
const AGENT_PLATFORM_CHECKS_PRIORITY_ORDERED = [...AUTHENTICATION_CHECK_PRIORITY_ORDERED, AGENT_PLATFORM_DISABLED_BY_USER];
const SANDBOX_CHECKS_PRIORITY_ORDERED = [
	SANDBOX_UNSUPPORTED_PLATFORM,
	SANDBOX_MISSING_DEPENDENCIES,
	SANDBOX_DISABLED_BY_USER
];
const CHECKS_PER_FEATURE = {
	[AUTHENTICATION]: AUTHENTICATION_CHECK_PRIORITY_ORDERED,
	[CODE_SUGGESTIONS]: CODE_SUGGESTIONS_CHECKS_PRIORITY_ORDERED,
	[CHAT]: CHAT_CHECKS_PRIORITY_ORDERED,
	[CHAT_TERMINAL_CONTEXT]: [...CHAT_CHECKS_PRIORITY_ORDERED, CHAT_INCLUDE_TERMINAL_CONTEXT_UNAVAILABLE],
	[AGENTIC_CHAT]: AGENTIC_CHAT_CHECKS_PRIORITY_ORDERED,
	[AGENT_PLATFORM]: AGENT_PLATFORM_CHECKS_PRIORITY_ORDERED,
	[FLOWS]: FLOWS_CHECKS_PRIORITY_ORDERED,
	[SANDBOX]: SANDBOX_CHECKS_PRIORITY_ORDERED
};
const STATE_CHECK_USER_READABLE_LABELS = {
	[AUTHENTICATION_REQUIRED]: "User is authenticated",
	[INVALID_TOKEN]: "Token is valid",
	[SUGGESTIONS_AUTHENTICATION_REQUIRED]: "User is authenticated",
	[SUGGESTIONS_NO_LICENSE]: "Valid GitLab license",
	[CHAT_AUTHENTICATION_REQUIRED]: "User is authenticated",
	[CHAT_NO_LICENSE]: "Valid GitLab license",
	[DUO_DISABLED_FOR_PROJECT]: "GitLab Duo is enabled for the open project(s)",
	[UNSUPPORTED_GITLAB_VERSION]: "The GitLab instance version supports Code Suggestions",
	[UNSUPPORTED_LANGUAGE]: "Code suggestions are supported for the current file's language",
	[DISABLED_LANGUAGE]: "Code suggestions are enabled for the current file's language",
	[SUGGESTIONS_API_ERROR]: "Code Suggestions API connection is working",
	[SUGGESTIONS_NO_DEFAULT_NAMESPACE]: "User has selected a default GitLab Duo namespace",
	[SUGGESTIONS_NO_CREDITS]: "User has enough credits to continue Code Suggestions usage for this billing period",
	[CHAT_DISABLED_BY_USER]: "Non-Agentic Chat is enabled in settings",
	[SUGGESTIONS_DISABLED_BY_USER]: "Code Suggestions is enabled in settings",
	[SUGGESTIONS_FILE_EXCLUDED]: "Current file is not excluded by project exclusion rules",
	[CHAT_INCLUDE_TERMINAL_CONTEXT_UNAVAILABLE]: "Include terminal context is enabled for user",
	[AGENT_PLATFORM_DISABLED_BY_USER]: "Agent Platform is enabled in settings",
	[AGENTIC_CHAT_NO_SUPPORT]: "Agentic Chat is supported for the current project",
	[CLASSIC_CHAT_NO_LICENSE]: "Classic Chat is available with the current GitLab Duo license",
	[FLOWS_INSTANCE_FLAG_DISABLED]: "Flows feature flag is enabled on the GitLab instance",
	[SANDBOX_DISABLED_BY_USER]: "Process sandboxing is enabled in settings",
	[SANDBOX_UNSUPPORTED_PLATFORM]: "Process sandboxing is supported on this platform",
	[SANDBOX_MISSING_DEPENDENCIES]: "System dependencies required for process sandboxing are installed"
};
const STATE_CHECK_DISABLED_LABELS = {
	[AUTHENTICATION_REQUIRED]: "Authentication Required",
	[SUGGESTIONS_AUTHENTICATION_REQUIRED]: "Authentication Required",
	[CHAT_AUTHENTICATION_REQUIRED]: "Authentication Required",
	[INVALID_TOKEN]: "Invalid Token",
	[SUGGESTIONS_NO_LICENSE]: "Invalid License",
	[CHAT_NO_LICENSE]: "Invalid License",
	[CLASSIC_CHAT_NO_LICENSE]: "Invalid License",
	[SUGGESTIONS_NO_DEFAULT_NAMESPACE]: "No Default Namespace Selected",
	[SUGGESTIONS_NO_CREDITS]: "Out of GitLab Credits",
	[DUO_DISABLED_FOR_PROJECT]: "GitLab Duo Disabled for Project",
	[UNSUPPORTED_GITLAB_VERSION]: "Unsupported GitLab Version",
	[UNSUPPORTED_LANGUAGE]: "Unsupported Language",
	[SUGGESTIONS_API_ERROR]: "API Error",
	[DISABLED_LANGUAGE]: "Disabled for Language",
	[SUGGESTIONS_DISABLED_BY_USER]: "Disabled by User",
	[SUGGESTIONS_FILE_EXCLUDED]: "File Excluded"
};
//#endregion
//#region src/external_interfaces.ts
const LsConnection = createInterfaceId("LsConnection");
const LsTextDocuments = createInterfaceId("LsTextDocuments");
//#endregion
//#region src/constants.ts
const GITLAB_API_BASE_URL = "https://gitlab.com";
//#endregion
//#region src/repository_provider/repository_provider.ts
const RepositoryProvider = createInterfaceId("RepositoryProvider");
//#endregion
//#region src/repository_provider/index.ts
const RepositoryEndpoints = {
	GET_REPOSITORIES: "$/gitlab/getRepositories",
	SELECT_PROJECT: "$/gitlab/selectProject",
	CLEAR_PROJECT: "$/gitlab/clearProject",
	REPOSITORIES_CHANGED: "$/gitlab/repositoriesChanged"
};
const RemoteUrlSchema = z.object({
	url: z.string(),
	type: z.enum([
		"fetch",
		"push",
		"both"
	])
});
const RemoteSchema = z.object({
	name: z.string(),
	urls: z.array(RemoteUrlSchema)
});
const RepositorySchema = z.object({
	rootFsPath: z.string(),
	folderName: z.string(),
	remotes: z.array(RemoteSchema)
});
const GitRemoteUrlPointerSchema = z.object({
	urlEntry: RemoteUrlSchema,
	remote: RemoteSchema,
	repository: RepositorySchema
});
const ProjectInRepositorySchema = z.object({
	project: z.object({
		name: z.string(),
		namespaceWithPath: z.string(),
		webUrl: z.string()
	}),
	account: z.object({
		id: z.string(),
		username: z.string(),
		restId: z.number(),
		instanceUrl: z.string()
	}),
	pointer: GitRemoteUrlPointerSchema,
	initializationType: z.enum(["detected", "selected"])
});
const RepositoryWithoutProjectSchema = z.object({
	type: z.literal("none"),
	repository: RepositorySchema
});
const SingleProjectRepositorySchema = z.object({
	type: z.literal("single"),
	repository: RepositorySchema,
	projects: z.array(ProjectInRepositorySchema),
	selectedProject: ProjectInRepositorySchema
});
const MultipleProjectRepositorySchema = z.object({
	type: z.literal("multiple"),
	repository: RepositorySchema,
	projects: z.array(ProjectInRepositorySchema)
});
const SelectedProjectRepositorySchema = z.object({
	type: z.literal("selected"),
	repository: RepositorySchema,
	projects: z.array(ProjectInRepositorySchema),
	selectedProject: ProjectInRepositorySchema
});
const RepositoryStateSchema = z.union([
	RepositoryWithoutProjectSchema,
	SingleProjectRepositorySchema,
	MultipleProjectRepositorySchema,
	SelectedProjectRepositorySchema
]);
const GetRepositoriesResponseSchema = z.object({ repositories: z.array(RepositoryStateSchema) });
const GetRepositoriesRequest = declareRequest(RepositoryEndpoints.GET_REPOSITORIES).withResponse(GetRepositoriesResponseSchema).build();
const SelectProjectRequestSchema = ProjectInRepositorySchema;
const ClearProjectRequestSchema = z.object({ rootFsPath: z.string() });
const SelectProjectRequest = declareRequest(RepositoryEndpoints.SELECT_PROJECT).withParams(SelectProjectRequestSchema).build();
const ClearProjectRequest = declareRequest(RepositoryEndpoints.CLEAR_PROJECT).withParams(ClearProjectRequestSchema).build();
const RepositoriesChangedNotificationType = new NotificationType(RepositoryEndpoints.REPOSITORIES_CHANGED);
//#endregion
//#region src/create_fallback_service.ts
/**
* Generic utility function that tries a sequence of async functions in order
* and returns the first successful result or throws the last error
* @param logger Logger object for showing errors of previous fallbacks while debugging.
* @param fns Array of async functions to try
* @param args Arguments to pass to each function
* @returns Result from the first successful function
* @throws Last error encountered if all functions fail
*/
function createFallbackFn(logger, fns) {
	if (fns.length === 0) throw new Error("No functions provided");
	return async (...args) => {
		let lastError = null;
		for (const fn of fns) try {
			return await fn(...args);
		} catch (error) {
			logger.debug(`failed to call function ${fn?.name ?? "unknown"}, will try to fall back: ${error instanceof Error ? error.message : String(error)}`);
			lastError = error instanceof Error ? error : new Error(String(error));
		}
		throw lastError || /* @__PURE__ */ new Error("All functions failed");
	};
}
/**
* Take a list of services that implement an identical interface and return an
* object with methods that iterate over the service options, returning the first
* success or throwing the last failure.
* @param logger Logger object for showing errors of previous fallbacks while debugging.
* @param services Array of services to merge into fallback service
* @returns Service object of all services merged together
*/
function createFallbackService(logger, services) {
	if (services.length === 0) throw new Error("No services provided");
	const sortedServices = sortBy(services, (s) => s.priority).reverse();
	const serviceProxy = {};
	const getAllPropertyNames = (obj) => {
		const props = /* @__PURE__ */ new Set();
		const builtInPrototypes = [
			Object.prototype,
			Array.prototype,
			Function.prototype,
			Number.prototype,
			String.prototype,
			Boolean.prototype,
			Date.prototype,
			RegExp.prototype,
			Error.prototype
		];
		let current = obj;
		while (current && !builtInPrototypes.includes(current)) {
			Object.getOwnPropertyNames(current).forEach((name) => props.add(name));
			current = Object.getPrototypeOf(current);
		}
		return Array.from(props);
	};
	const firstService = services[0];
	if (!firstService) throw new Error("No services provided");
	getAllPropertyNames(firstService).filter((p) => typeof firstService[p] === "function").forEach((method) => {
		serviceProxy[method] = createFallbackFn(logger, sortedServices.map((s) => s[method].bind(s)));
	});
	return serviceProxy;
}
//#endregion
export { AGENTIC_CHAT, AGENTIC_CHAT_CHECKS_PRIORITY_ORDERED, AGENTIC_CHAT_NO_SUPPORT, AGENT_PLATFORM, AGENT_PLATFORM_CHECKS_PRIORITY_ORDERED, AGENT_PLATFORM_DISABLED_BY_USER, AUTHENTICATION, AUTHENTICATION_CHECK_PRIORITY_ORDERED, AUTHENTICATION_REQUIRED, AbortError, CHAT, CHAT_AUTHENTICATION_REQUIRED, CHAT_CHECKS_PRIORITY_ORDERED, CHAT_DISABLED_BY_USER, CHAT_INCLUDE_TERMINAL_CONTEXT_UNAVAILABLE, CHAT_NO_LICENSE, CHAT_TERMINAL_CONTEXT, CHECKS_PER_FEATURE, CIRCUIT_BREAK_INTERVAL_MS, CLASSIC_CHAT_NO_LICENSE, CODE_SUGGESTIONS, CODE_SUGGESTIONS_CHECKS_PRIORITY_ORDERED, CircuitBreakerState, ClearProjectRequest, ClientFeatureFlags, DEFAULT_BACKOFF_MULTIPLIER, DEFAULT_INITIAL_BACKOFF_MS, DEFAULT_MAX_BACKOFF_MS, DISABLED_LANGUAGE, DUO_DISABLED_FOR_PROJECT, DUO_NAMESPACE_NOT_ENTITLED_MESSAGE, DUO_NO_NAMESPACE_DETECTED_MESSAGE, DUO_USAGE_QUOTA_EXCEEDED_MESSAGE, _DefaultInstanceFeatu as DefaultInstanceFeatureFlagsService, _DefaultProjectServic as DefaultProjectService, DefaultSimpleApiClient, _DefaultUserService as DefaultUserService, EventEmitterImpl, ExponentialBackoffCircuitBreaker, FLOWS, FLOWS_CHECKS_PRIORITY_ORDERED, FLOWS_INSTANCE_FLAG_DISABLED, FeatureFlagService, FeatureStateManager, FetchError, FixedTimeCircuitBreaker, GID_NAMESPACE_GROUP, GID_NAMESPACE_ISSUE, GID_NAMESPACE_MERGE_REQUEST, GID_NAMESPACE_PROJECT, GITLAB_API_BASE_URL, GetRepositoriesRequest, GitLabApiService, GitRemoteUrlPointerSchema, GraphQLError, INVALID_TOKEN, InstanceFeatureFlags, InstanceFeatureFlagsService, LsConnection, LsTextDocuments, MAX_ERRORS_BEFORE_CIRCUIT_BREAK, MaxAttemptsCircuitBreaker, NetworkError, ProjectService, RESTError, RepositoriesChangedNotificationType, RepositoryEndpoints, RepositoryProvider, SANDBOX, SANDBOX_CHECKS_PRIORITY_ORDERED, SANDBOX_DISABLED_BY_USER, SANDBOX_MISSING_DEPENDENCIES, SANDBOX_UNSUPPORTED_PLATFORM, STATE_CHECK_DISABLED_LABELS, STATE_CHECK_USER_READABLE_LABELS, SUGGESTIONS_API_ERROR, SUGGESTIONS_AUTHENTICATION_REQUIRED, SUGGESTIONS_DISABLED_BY_USER, SUGGESTIONS_FILE_EXCLUDED, SUGGESTIONS_NO_CREDITS, SUGGESTIONS_NO_DEFAULT_NAMESPACE, SUGGESTIONS_NO_LICENSE, SelectProjectRequest, TimeoutError, UNSUPPORTED_GITLAB_VERSION, UNSUPPORTED_LANGUAGE, UserService, addQueryParams, applyEol, asyncDebounce, checkToken, classifyDuoAccessError, convertToHttpUrl, createFallbackFn, createFallbackService, createPendingPromise, deepCamelCaseKeys, deepSnakeCaseKeys, detectEol, diffEmitter, doNotAwait, ensureEndsWithSlash, ensureRelativePath, extractGraphQLOperationLabel, formatTimeAgo, getByteSize, getLanguageServerVersion, getUserAgent, handleFetchError, ifVersionGte, isApiError, isBunRuntime, isDuoAccessMessage, isFetchError, isMissingDefaultDuoGroupError, isNot4xxFailure, isResponseError, isSanitizedError, isTransportError, normalizeToLf, notNullOrUndefined, splitLines, stackToArray, toGitLabGid, truncateToByteLimit, tryParseGitLabGid, tryParseGitLabGidToString, tryParseUrl, versionRequest, withTimeout };

//# sourceMappingURL=index.mjs.map